import React, { useEffect, useMemo, useState } from 'react';
import { BadgeIndianRupee, Check, CreditCard, Landmark, QrCode, ShieldCheck, WalletCards } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { getApiUrl } from '../config/api';

const subscriptionPlans = {
  monthly: [
    {
      code: 'basic',
      planType: 'basic',
      name: 'Basic Plan',
      amount: 299,
      price: '₹299',
      period: 'month',
      description: 'Essential research access for disciplined market tracking.',
      features: ['Basic research reports', 'Segment financials', 'Weekly market summary'],
      cta: 'Get Basic Access',
    },
    {
      code: 'premium',
      planType: 'premium',
      name: 'Premium Plan',
      amount: 999,
      price: '₹999',
      period: 'month',
      description: 'Institutional-grade analysis for deeper investment decisions.',
      features: ['Everything in Basic', 'Premium reports', 'DCF and scenario analysis'],
      cta: 'Upgrade to Premium',
    },
  ],
  yearly: [
    {
      code: 'basic',
      planType: 'basic_yearly',
      name: 'Basic Plan',
      amount: 2999,
      price: '₹2,999',
      period: 'year',
      discount: '16% off',
      description: 'A full year of essential research with better value.',
      features: ['Basic research reports', 'Segment financials', 'Weekly market summary'],
      cta: 'Get Basic Access',
    },
    {
      code: 'premium',
      planType: 'premium_yearly',
      name: 'Premium Plan',
      amount: 8999,
      price: '₹8,999',
      period: 'year',
      discount: '25% off',
      description: 'Annual premium intelligence for serious subscribers.',
      features: ['Everything in Basic', 'Premium reports', 'DCF and scenario analysis'],
      cta: 'Upgrade to Premium',
    },
  ],
};

const paymentMethods = [
  { code: 'upi', name: 'QR Code UPI Scan', description: 'Scan and pay from any UPI app.', icon: QrCode },
  { code: 'netbanking', name: 'Net Banking', description: 'Pay securely from your bank account.', icon: Landmark },
  { code: 'credit_card', name: 'Credit Card', description: 'Visa, Mastercard, RuPay and more.', icon: CreditCard },
  { code: 'debit_card', name: 'Debit Card', description: 'Instant payment using a debit card.', icon: WalletCards },
];

const safePlanCode = (value) => (value === 'premium' ? 'premium' : 'basic');
const safeBilling = (value) => (value === 'yearly' ? 'yearly' : 'monthly');

const loadRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(true);
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.onload = () => resolve(true);
  script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
  document.body.appendChild(script);
});

const readResponseJson = async (response, fallbackMessage) => {
  if (response.bodyUsed) {
    return { message: fallbackMessage };
  }

  try {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    return { message: fallbackMessage };
  }
};

const methodConfig = (method) => {
  if (method === 'upi') {
    return { upi: true, netbanking: false, card: false, wallet: false };
  }
  if (method === 'netbanking') {
    return { upi: false, netbanking: true, card: false, wallet: false };
  }
  if (method === 'credit_card' || method === 'debit_card') {
    return { upi: false, netbanking: false, card: true, wallet: false };
  }
  return { upi: true, netbanking: true, card: true, wallet: false };
};

const Subscription = () => {
  const initialParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [billing, setBilling] = useState(() => safeBilling(initialParams.get('billing')));
  const [selectedPlanCode, setSelectedPlanCode] = useState(() => safePlanCode(initialParams.get('plan')));
  const checkoutMode = useMemo(() => (initialParams.get('mode') || 'order').toLowerCase(), [initialParams]);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [downgradeScheduled, setDowngradeScheduled] = useState(false);

  const plans = subscriptionPlans[billing];
  const selectedPlan = plans.find((plan) => plan.code === selectedPlanCode) || plans[0];
  const selectedPaymentMethod = paymentMethods.find((method) => method.code === paymentMethod) || paymentMethods[0];

  const isActivePlan = (plan) =>
    subscription?.subscription_status === 'ACTIVE' &&
    subscription?.plan_type?.toLowerCase() === plan.code &&
    subscription?.billing_cycle?.toLowerCase() === billing;

  const isSelectedPlanActive = isActivePlan(selectedPlan);

  const formatINR = (amount) => `₹${Math.round(amount).toLocaleString('en-IN')}`;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('plan', selectedPlanCode);
    params.set('billing', billing);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [billing, selectedPlanCode]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoadingUser(true);
        const API = getApiUrl();
        const resp = await fetch(`${API}/auth/me`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (resp.ok) {
          setUser(await resp.json());
        } else {
          window.location.href = `/signin?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
          return;
        }

        try {
          const subResp = await fetch(`${API}/payments/subscription/current`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (subResp.ok && subResp.status !== 204) {
            const text = await subResp.text();
            setSubscription(text ? JSON.parse(text) : null);
          }
        } catch (_) {
          // subscription fetch is non-critical
        }
      } catch (e) {
        window.location.href = `/signin?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, []);

  useEffect(() => {
    setDowngradeScheduled(false);
  }, [selectedPlanCode, billing]);

  useEffect(() => {
    const isCurrentPlan =
      subscription?.subscription_status === 'ACTIVE' &&
      subscription?.plan_type?.toLowerCase() === selectedPlanCode &&
      subscription?.billing_cycle?.toLowerCase() === billing;

    if (!subscription || !user || isCurrentPlan) {
      setUpgradePreview(null);
      return;
    }

    const controller = new AbortController();
    const fetchPreview = async () => {
      setUpgradePreviewLoading(true);
      try {
        const API = getApiUrl();
        const resp = await fetch(
          `${API}/payments/razorpay/upgrade-preview?plan=${selectedPlanCode}&billing=${billing}`,
          { credentials: 'include', signal: controller.signal }
        );
        if (resp.ok) {
          setUpgradePreview(await resp.json());
        } else {
          setUpgradePreview(null);
        }
      } catch (e) {
        if (e.name !== 'AbortError') setUpgradePreview(null);
      } finally {
        setUpgradePreviewLoading(false);
      }
    };
    fetchPreview();
    return () => controller.abort();
  }, [selectedPlanCode, billing, subscription, user]);

  const handleScheduleDowngrade = async () => {
    if (!user || downgradeLoading) return;
    setDowngradeLoading(true);
    try {
      const API = getApiUrl();
      const resp = await fetch(`${API}/payments/subscription/schedule-downgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_type: selectedPlanCode, billing_cycle: billing }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setSubscription(data);
        setDowngradeScheduled(true);
      }
    } catch (_) {
      // silent
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!user || checkoutLoading) {
      return;
    }

    try {
      setCheckoutLoading(true);
      setCheckoutError('');
      const API = getApiUrl();
      const createEndpoint = checkoutMode === 'subscription'
        ? `${API}/payments/razorpay/create-subscription`
        : `${API}/payments/razorpay/create-order`;

      const resp = await fetch(createEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_type: selectedPlanCode,
          billing_cycle: billing,
          origin_url: window.location.origin + '/subscription',
        }),
      });

      if (!resp.ok) {
        const errorData = await readResponseJson(resp, 'Unable to create Razorpay order');
        throw new Error(errorData.message || 'Unable to create Razorpay order');
      }

      const checkout = await readResponseJson(resp, 'Unexpected response from payment service');
      const keyId = checkout.key_id || checkout.keyId;
      const checkoutId = checkout.order_id || checkout.orderId;
      const amount = checkout.amount;
      const currency = checkout.currency;

      if (!keyId || !checkoutId || !amount || !currency) {
        throw new Error('Payment service did not return Razorpay checkout details');
      }

      await loadRazorpayScript();

      const razorpay = new window.Razorpay({
        key: keyId,
        ...(checkoutMode === 'subscription'
          ? { subscription_id: checkoutId }
          : { order_id: checkoutId }),
        amount: Math.round(Number(amount) * 100),
        currency,
        name: 'The B Tree',
        description: `${selectedPlan.name} - ${billing === 'yearly' ? 'Yearly' : 'Monthly'}`,
        prefill: {
          name: user.name || user.full_name || '',
          email: user.email || '',
        },
        method: methodConfig(paymentMethod),
        notes: {
          plan_type: selectedPlanCode,
          billing_cycle: billing,
          payment_method: selectedPaymentMethod.name,
        },
        handler: async (response) => {
          const verifyEndpoint = checkoutMode === 'subscription'
            ? `${API}/payments/razorpay/verify`
            : `${API}/payments/razorpay/verify-order`;

          const verifyBody = checkoutMode === 'subscription'
            ? {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }
            : {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

          const verifyResp = await fetch(verifyEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(verifyBody),
          });

          if (!verifyResp.ok) {
            const verifyError = await readResponseJson(verifyResp, 'Payment completed, but verification failed. Please contact support with your payment ID.');
            setCheckoutError(verifyError.message || 'Payment completed, but verification failed. Please contact support with your payment ID.');
            setCheckoutLoading(false);
            return;
          }

          window.location.href = 'https://www.thebtree.com';
        },
        modal: {
          ondismiss: () => setCheckoutLoading(false),
        },
        theme: {
          color: '#0D1B2A',
        },
      });

      razorpay.on('payment.failed', (response) => {
        setCheckoutError(response?.error?.description || 'Razorpay payment failed. Please try another method.');
        setCheckoutLoading(false);
      });

      razorpay.open();
    } catch (error) {
      setCheckoutError(error.message || 'Unable to start Razorpay checkout');
      setCheckoutLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F7F5F2]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-14">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#8B6F18]">
              <ShieldCheck className="h-4 w-4" />
              Secure Subscription
            </div>
            <h1 className="mt-5 text-4xl md:text-5xl font-[Cormorant_Garamond] font-medium text-slate-950">
              Choose your access
            </h1>
            <p className="mt-2 max-w-2xl text-slate-600">
              Select a subscription plan and continue with Razorpay using your preferred payment method.
            </p>
          </div>

          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            {['monthly', 'yearly'].map((cycle) => (
              <button
                key={cycle}
                type="button"
                onClick={() => setBilling(cycle)}
                className={`h-10 rounded-full px-5 text-sm font-semibold capitalize transition-all ${
                  billing === cycle
                    ? 'bg-slate-950 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-950'
                }`}
              >
                {cycle}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.08fr)_minmax(380px,0.92fr)]">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-slate-500">Subscription plans</div>
                <h2 className="mt-2 text-2xl font-[Cormorant_Garamond] font-semibold text-slate-950">
                  {billing === 'yearly' ? 'Yearly access' : 'Monthly access'}
                </h2>
              </div>
              {billing === 'yearly' && (
                <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                  Best value
                </div>
              )}
            </div>

            <RadioGroup value={selectedPlanCode} onValueChange={setSelectedPlanCode} className="grid gap-4">
              {plans.map((plan) => {
                const isSelected = selectedPlan.planType === plan.planType;
                const isCurrent = isActivePlan(plan);
                return (
                  <Label
                    key={plan.planType}
                    htmlFor={plan.planType}
                    className={`rounded-xl border p-5 transition-all ${
                      isCurrent
                        ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                        : isSelected
                        ? 'cursor-pointer border-[#D4AF37] bg-[#FFF9EA] shadow-[0_16px_40px_rgba(212,175,55,0.16)]'
                        : 'cursor-pointer border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <RadioGroupItem id={plan.planType} value={plan.code} disabled={isCurrent} className="mt-1 border-slate-400" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xl font-semibold text-slate-950">{plan.name}</span>
                              {isCurrent && (
                                <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                                  Current plan
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm leading-6 text-slate-600">{plan.description}</p>
                          </div>
                          <div className="sm:text-right">
                            <div className="text-3xl font-bold text-slate-950">{plan.price}</div>
                            <div className="text-sm text-slate-500">/{plan.period}</div>
                            {plan.discount && (
                              <div className="mt-1 text-sm font-semibold text-emerald-700">{plan.discount}</div>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 grid gap-2 sm:grid-cols-3">
                          {plan.features.map((feature) => (
                            <div key={feature} className="flex items-center gap-2 text-sm text-slate-700">
                              <Check className="h-4 w-4 text-[#B99425]" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Label>
                );
              })}
            </RadioGroup>
          </section>

          <aside className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-slate-500">Razorpay checkout</div>
                <h2 className="mt-2 text-2xl font-[Cormorant_Garamond] font-semibold text-slate-950">
                  Payment method
                </h2>
              </div>
              <BadgeIndianRupee className="h-9 w-9 rounded-full bg-[#0D1B2A] p-2 text-[#D4AF37]" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              {upgradePreviewLoading ? (
                <div className="flex items-center justify-center py-3">
                  <div className="animate-spin h-5 w-5 border-2 border-[#C8A84B] border-t-transparent rounded-full" />
                </div>
              ) : upgradePreview?.move_type === 'UPGRADE' ? (
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{selectedPlan.name}</div>
                      <div className="mt-1 text-sm capitalize text-slate-600">{billing} · Immediate upgrade</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-950">{formatINR(upgradePreview.charge_amount)}</div>
                      {upgradePreview.pro_rata_credit > 0 && (
                        <div className="text-xs font-medium text-emerald-600">after credit</div>
                      )}
                    </div>
                  </div>
                  {upgradePreview.pro_rata_credit > 0 && (
                    <div className="pt-2 border-t border-slate-200 space-y-1.5 text-xs">
                      <div className="flex justify-between text-slate-600">
                        <span>{selectedPlan.name} ({billing})</span>
                        <span>{formatINR(upgradePreview.new_plan_amount)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-700">
                        <span>Credit ({upgradePreview.days_remaining}d unused of current plan)</span>
                        <span>−{formatINR(upgradePreview.pro_rata_credit)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-slate-950 pt-1 border-t border-slate-200">
                        <span>You pay today</span>
                        <span>{formatINR(upgradePreview.charge_amount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{selectedPlan.name}</div>
                      <div className="mt-1 text-sm capitalize text-slate-600">{billing} subscription</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-950">{selectedPlan.price}</div>
                      {selectedPlan.discount && (
                        <div className="text-sm font-semibold text-emerald-700">{selectedPlan.discount}</div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    Plan sent to checkout: <span className="font-mono text-slate-700">{selectedPlan.planType}</span>
                  </div>
                </>
              )}
            </div>

            {upgradePreview?.move_type === 'DOWNGRADE' && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <p className="font-semibold mb-1">Downgrade takes effect at renewal</p>
                <p className="text-xs leading-5">You keep your current plan until it expires. The downgrade to {selectedPlan.name} ({billing}) activates at renewal — no refunds issued.</p>
                {subscription?.end_date && (
                  <p className="mt-2 text-xs font-medium">
                    Current plan expires: {new Date(subscription.end_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            )}

            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className={`mt-5 grid gap-3 ${upgradePreview?.move_type === 'DOWNGRADE' ? 'opacity-40 pointer-events-none' : ''}`}>
              {paymentMethods.map((method) => {
                const Icon = method.icon;
                const isSelected = paymentMethod === method.code;
                return (
                  <Label
                    key={method.code}
                    htmlFor={method.code}
                    className={`cursor-pointer rounded-xl border p-4 transition-all ${
                      isSelected ? 'border-[#0D1B2A] bg-slate-950 text-white' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`rounded-lg p-2 ${isSelected ? 'bg-white/10 text-[#D4AF37]' : 'bg-slate-100 text-slate-700'}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{method.name}</div>
                        <div className={`mt-1 text-sm ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                          {method.description}
                        </div>
                      </div>
                      <RadioGroupItem id={method.code} value={method.code} className={isSelected ? 'border-white text-white' : ''} />
                    </div>
                  </Label>
                );
              })}
            </RadioGroup>

            <div className="mt-6 space-y-3">
              {upgradePreview?.move_type === 'DOWNGRADE' ? (
                downgradeScheduled ||
                (subscription?.pending_downgrade_plan_type === selectedPlanCode &&
                  subscription?.pending_downgrade_billing_cycle === billing) ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700 text-center font-medium">
                    ✓ Downgrade to {selectedPlan.name} scheduled at renewal
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={handleScheduleDowngrade}
                    disabled={downgradeLoading || !user}
                    className="h-12 w-full rounded-full bg-slate-900 font-bold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downgradeLoading ? 'Scheduling...' : `Schedule Downgrade to ${selectedPlan.name}`}
                  </Button>
                )
              ) : (
                <Button
                  type="button"
                  onClick={handleCheckout}
                  disabled={loadingUser || !user || checkoutLoading || isSelectedPlanActive}
                  className="h-12 w-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] font-bold text-black shadow-[0_12px_30px_rgba(212,175,55,0.25)] hover:shadow-[0_16px_38px_rgba(212,175,55,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading
                    ? 'Opening Razorpay...'
                    : isSelectedPlanActive
                    ? 'Already on this plan'
                    : upgradePreview?.move_type === 'UPGRADE'
                    ? `Upgrade Now · ${formatINR(upgradePreview.charge_amount)}`
                    : `${selectedPlan.cta} - ${selectedPlan.price}`}
                </Button>
              )}

              {checkoutError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {checkoutError}
                </div>
              )}

              <p className="text-xs leading-5 text-slate-500">
                {upgradePreview?.move_type === 'DOWNGRADE'
                  ? 'You will keep premium access until your current plan expires.'
                  : upgradePreview?.move_type === 'UPGRADE'
                  ? 'Unused value from your current plan is credited toward this upgrade.'
                  : 'Your selected plan and preferred method are passed into the Razorpay checkout request before payment starts.'}
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Subscription;
